import { useMemo } from "react";
import {
    useIsOrganizationChat,
    useMarkAllRead,
    useThreads,
    useUnreadMessagesCount,
} from "@api/chat.api";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import { InfiniteScrollContainer } from "@design-system/InfiniteScrollContainer/InfiniteScrollContainer";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CheckIcon } from "@icons";
import { ThreadWithWorkspaceName } from "@common/schemas/chat.schema";
import styles from "./Chat.module.scss";
import { ChatDrawerHeader } from "./ChatDrawerHeader";
import { Thread as ThreadComponent } from "./Thread";

interface ThreadProps {
    setSelectedThread: (thread: ThreadWithWorkspaceName) => void;
}

export const Threads = ({ setSelectedThread }: ThreadProps) => {
    const {
        data: threadsPages,
        fetchNextPage: fetchNextThreads,
        hasNextPage: hasMoreThreads,
        isFetchingNextPage: isFetchingMoreThreads,
        status: threadsStatus,
        refetch: refetchThreads,
    } = useThreads();

    const threads = useMemo(
        () => threadsPages?.pages.flatMap((p) => p.threads ?? []) ?? [],
        [threadsPages],
    );

    const { data: unreadCount } = useUnreadMessagesCount();
    const { mutate: markAllRead, isPending: isMarkingAllRead } =
        useMarkAllRead();
    const toaster = useToaster();
    const isOrganizationChat = useIsOrganizationChat();
    const hasUnreadMessages = (unreadCount ?? 0) > 0 && !isOrganizationChat;

    const handleMarkAllRead = () => {
        markAllRead(undefined, {
            onError: () =>
                toaster.error("Failed to mark all as read. Please try again."),
        });
    };

    const renderThreadsContent = () => {
        if (threadsStatus === "pending") {
            return (
                <div className={styles.threadsState}>
                    <Loader />
                </div>
            );
        }

        if (threadsStatus === "error") {
            return (
                <div className={styles.threadsState}>
                    <p className={styles.threadsErrorMessage}>
                        Could not load threads.
                    </p>
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={() => refetchThreads()}
                    >
                        Retry
                    </Button>
                </div>
            );
        }

        if (threads.length === 0) {
            return <div className={styles.threadsState}>No threads yet.</div>;
        }

        return (
            <InfiniteScrollContainer
                hasNextPage={hasMoreThreads ?? false}
                loadMore={fetchNextThreads}
                isFetchingMoreMessages={isFetchingMoreThreads}
                direction="down"
                height="100%"
            >
                {threads.map((thread) => (
                    <ThreadComponent
                        key={thread.id}
                        thread={thread}
                        workspaceName={thread.workspaceName}
                        onClick={() => setSelectedThread(thread)}
                    />
                ))}
                {isFetchingMoreThreads ? (
                    <div className={styles.threadsLoadMore}>
                        <Loader inline />
                    </div>
                ) : null}
            </InfiniteScrollContainer>
        );
    };

    return (
        <>
            <ChatDrawerHeader />

            <div className={styles.threadsList}>
                {hasUnreadMessages ? (
                    <HStack
                        gap={8}
                        align="center"
                        justify="end"
                        className={styles.threadsActions}
                    >
                        <Tooltip content="Mark all read">
                            <Button
                                iconOnly
                                variant="secondary"
                                size="sm"
                                onClick={handleMarkAllRead}
                                disabled={isMarkingAllRead}
                                aria-label="Mark all read"
                            >
                                <CheckIcon />
                            </Button>
                        </Tooltip>
                    </HStack>
                ) : null}
                <div className={styles.threadsScroll}>
                    {renderThreadsContent()}
                </div>
            </div>
        </>
    );
};
